import { useState, useRef, useMemo, useEffect } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { GlassCard } from '../ui/GlassCard';
import { Calculator, TrendingDown, ToggleRight, ToggleLeft } from 'lucide-react';
import { cn } from '../../lib/utils';

// ─── Animated Number ─────────────────────────────────────────────────

function AnimatedNumber({
  value,
  prefix = '',
  suffix = '',
  decimals = 0,
  className,
}: {
  value: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const motionVal = useMotionValue(0);
  const rounded = useTransform(motionVal, (v) => {
    if (decimals > 0) return `${prefix}${v.toFixed(decimals)}${suffix}`;
    return `${prefix}${Math.round(v).toLocaleString()}${suffix}`;
  });

  useEffect(() => {
    const controls = animate(motionVal, value, {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94],
    });
    const unsub = rounded.on('change', (v) => {
      if (ref.current) ref.current.textContent = v;
    });
    return () => {
      controls.stop();
      unsub();
    };
  }, [value, motionVal, rounded]);

  return <span ref={ref} className={className}>{`${prefix}${decimals > 0 ? value.toFixed(decimals) : Math.round(value).toLocaleString()}${suffix}`}</span>;
}

// ─── Slider ──────────────────────────────────────────────────────────

function Slider({
  label,
  value,
  min,
  max,
  step,
  onChange,
  prefix = '',
  suffix = '',
  color = 'bg-vokentra',
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  prefix?: string;
  suffix?: string;
  color?: string;
}) {
  const pct = ((value - min) / (max - min)) * 100;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-text-secondary">{label}</span>
        <span className="text-xs font-semibold text-text-primary tabular-nums">
          {prefix}{value.toLocaleString()}{suffix}
        </span>
      </div>
      <div className="relative h-5 flex items-center">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
        />
        {/* Track */}
        <div className="w-full h-1.5 rounded-full bg-white/[0.06] relative overflow-hidden">
          <motion.div
            className={cn('h-full rounded-full', color)}
            style={{ width: `${pct}%` }}
            transition={{ duration: 0.1 }}
          />
        </div>
        {/* Thumb */}
        <motion.div
          className={cn(
            'absolute w-4 h-4 rounded-full border-2 border-white/20 shadow-lg',
            color
          )}
          style={{
            left: `calc(${pct}% - 8px)`,
          }}
          transition={{ duration: 0.1 }}
        />
      </div>
    </div>
  );
}

// ─── Gauge ───────────────────────────────────────────────────────────

function SavingsGauge({ percentage }: { percentage: number }) {
  const ref = useRef<SVGSVGElement>(null);
  const isInView = useInView(ref, { once: true });
  const radius = 70;
  const stroke = 10;
  const normalizedRadius = radius - stroke / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const arc = circumference * 0.75; // 270-degree arc
  const strokeDashoffset = arc - (isInView ? percentage / 100 : 0) * arc;

  const color = percentage > 90 ? '#00d084' : percentage > 70 ? '#00bfff' : '#ffa500';

  return (
    <div className="flex flex-col items-center">
      <svg
        ref={ref}
        width={radius * 2}
        height={radius * 1.6}
        viewBox={`0 0 ${radius * 2} ${radius * 1.6}`}
      >
        {/* Background arc */}
        <circle
          cx={radius}
          cy={radius * 1.1}
          r={normalizedRadius}
          fill="transparent"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={stroke}
          strokeDasharray={arc}
          strokeDashoffset={circumference - arc}
          strokeLinecap="round"
          transform={`rotate(135 ${radius} ${radius * 1.1})`}
        />
        {/* Progress arc */}
        <motion.circle
          cx={radius}
          cy={radius * 1.1}
          r={normalizedRadius}
          fill="transparent"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={arc}
          strokeLinecap="round"
          transform={`rotate(135 ${radius} ${radius * 1.1})`}
          initial={{ strokeDashoffset: arc }}
          animate={isInView ? { strokeDashoffset: strokeDashoffset } : {}}
          transition={{ duration: 1.5, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.3 }}
        />
        {/* Percentage text */}
        <text
          x={radius}
          y={radius * 1.05}
          textAnchor="middle"
          fill="#ffffff"
          fontSize="22"
          fontWeight="bold"
          fontFamily="Inter, system-ui, sans-serif"
        >
          {percentage.toFixed(0)}%
        </text>
        <text
          x={radius}
          y={radius * 1.3}
          textAnchor="middle"
          fill="rgba(255,255,255,0.5)"
          fontSize="10"
          fontFamily="Inter, system-ui, sans-serif"
        >
          REDUCTION
        </text>
      </svg>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────

export function CostSimulation() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const [callVolume, setCallVolume] = useState(1_200_000);
  const [humanCostPerCall, setHumanCostPerCall] = useState(4.50);
  const [aiCostPerCall, setAiCostPerCall] = useState(0.35);
  const [showAnnual, setShowAnnual] = useState(false);

  const monthlyHumanTotal = useMemo(() => callVolume * humanCostPerCall, [callVolume, humanCostPerCall]);
  const monthlyAITotal = useMemo(() => callVolume * aiCostPerCall, [callVolume, aiCostPerCall]);
  const monthlySavings = useMemo(() => monthlyHumanTotal - monthlyAITotal, [monthlyHumanTotal, monthlyAITotal]);
  const savingsPercent = useMemo(
    () => (monthlyHumanTotal > 0 ? (monthlySavings / monthlyHumanTotal) * 100 : 0),
    [monthlySavings, monthlyHumanTotal]
  );

  const displayMultiplier = showAnnual ? 12 : 1;
  const staffCostMonthly = 5_400_000; // fixed for comparison context
  const infraCostMonthly = 94_000;

  const humanDisplayTotal = showAnnual ? staffCostMonthly * 12 : staffCostMonthly;
  const aiDisplayTotal = showAnnual ? infraCostMonthly * 12 : infraCostMonthly;
  const displaySavings = humanDisplayTotal - aiDisplayTotal;

  return (
    <div ref={ref}>
      <GlassCard
        variant="elevated"
        header={
          <div className="flex items-center gap-2">
            <Calculator className="w-4 h-4 text-success" />
            <span className="text-sm font-semibold text-text-primary">
              Cost Savings Simulation
            </span>
          </div>
        }
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Sliders */}
          <div className="space-y-5">
            <Slider
              label="Monthly Call Volume"
              value={callVolume}
              min={100_000}
              max={5_000_000}
              step={50_000}
              onChange={setCallVolume}
              suffix=" calls"
              color="bg-vokentra"
            />
            <Slider
              label="Human Agent Cost per Call"
              value={humanCostPerCall}
              min={1}
              max={15}
              step={0.10}
              onChange={setHumanCostPerCall}
              prefix="£"
              color="bg-warning"
            />
            <Slider
              label="AI Infrastructure Cost per Call"
              value={aiCostPerCall}
              min={0.05}
              max={2}
              step={0.01}
              onChange={setAiCostPerCall}
              prefix="£"
              color="bg-success"
            />

            {/* Annual toggle */}
            <button
              onClick={() => setShowAnnual(!showAnnual)}
              className="flex items-center gap-2 mt-2 group"
            >
              {showAnnual ? (
                <ToggleRight className="w-5 h-5 text-success" />
              ) : (
                <ToggleLeft className="w-5 h-5 text-text-muted" />
              )}
              <span className="text-xs text-text-secondary group-hover:text-text-primary transition-colors">
                Show Annual Projection
              </span>
            </button>
          </div>

          {/* Comparison */}
          <div className="space-y-4">
            {/* Human Call Center */}
            <motion.div
              className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]"
              initial={{ opacity: 0, x: 20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
                Human Call Center
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Avg cost/call</span>
                  <span className="text-text-primary font-medium tabular-nums">
                    £{humanCostPerCall.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Monthly calls</span>
                  <span className="text-text-primary font-medium tabular-nums">
                    {(callVolume / 1_000_000).toFixed(1)}M
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Staff cost</span>
                  <span className="text-text-primary font-medium tabular-nums">
                    £{showAnnual ? '64.8M' : '5.4M'}
                  </span>
                </div>
                <div className="h-px bg-white/[0.06] my-2" />
                <div className="flex justify-between">
                  <span className="text-sm font-semibold text-text-secondary">
                    {showAnnual ? 'Annual' : 'Monthly'} total
                  </span>
                  <AnimatedNumber
                    value={humanDisplayTotal / 1_000_000}
                    prefix="£"
                    suffix="M"
                    decimals={1}
                    className="text-sm font-bold text-warning tabular-nums"
                  />
                </div>
              </div>
            </motion.div>

            {/* AI Contact Center */}
            <motion.div
              className="p-4 rounded-xl bg-success/5 border border-success/10"
              initial={{ opacity: 0, x: 20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <h4 className="text-xs font-semibold text-success/70 uppercase tracking-wider mb-3">
                AI Contact Center
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Avg cost/call</span>
                  <span className="text-success font-medium tabular-nums">
                    £{aiCostPerCall.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Monthly calls</span>
                  <span className="text-text-primary font-medium tabular-nums">
                    {(callVolume / 1_000_000).toFixed(1)}M
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Infra cost</span>
                  <span className="text-success font-medium tabular-nums">
                    £{showAnnual ? '1.1M' : '94K'}
                  </span>
                </div>
                <div className="h-px bg-success/10 my-2" />
                <div className="flex justify-between">
                  <span className="text-sm font-semibold text-text-secondary">
                    {showAnnual ? 'Annual' : 'Monthly'} total
                  </span>
                  <AnimatedNumber
                    value={aiDisplayTotal / 1_000}
                    prefix="£"
                    suffix={showAnnual ? 'M' : 'K'}
                    decimals={showAnnual ? 1 : 0}
                    className="text-sm font-bold text-success tabular-nums"
                  />
                </div>
              </div>
            </motion.div>

            {/* Savings Banner */}
            <motion.div
              className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-success/10 to-success/5 border border-success/20"
              initial={{ opacity: 0, y: 10 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              <div className="flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-success" />
                <div>
                  <p className="text-xs text-success/70 font-medium">SAVINGS</p>
                  <AnimatedNumber
                    value={displaySavings / 1_000_000}
                    prefix="£"
                    suffix="M"
                    decimals={1}
                    className="text-xl font-bold text-success tabular-nums"
                  />
                </div>
              </div>
              <SavingsGauge percentage={savingsPercent} />
            </motion.div>
          </div>
        </div>
      </GlassCard>
    </div>
  );
}

export default CostSimulation;
